/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { GrabXRUMConfig } from "grabx"

export const DOWNGRADE_TO_WARN_MESSAGES = [
  "^Fetch error GET https://food-cms\\.grab\\.com/.*\\.(jpe?g|png|gif|webp|svg)(\\?.*)?$", // non-critical CMS asset/config fetches, we have retry mechanism
  "^Fetch error POST https://mcd-gateway\\.grabtaxi\\.com/v2/web/track(\\?|$)", // internal Scribe tracker, not an app error
  "^Fetch error POST https://public\\.grab\\.com/x-transmitter/v2/scribesdk-web/webVariable/(\\?|$)", // Scribe feature flag fetch error, 99.99% issue caused by user's network issue https://grab.slack.com/archives/C051MEW3C68/p1788758800888619
]

export const DEFAULT_IGNORE_ERROR_MESSAGES = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "zaloJSV2",
  "^XHR error (GET|POST|PUT|DELETE|PATCH)", // logged by datadog RUM, make noise to our error free session count
]

export const DEFAULT_DATADOG_RUM_CONFIG: GrabXRUMConfig = {
  sampleRate: 20,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: "mask",
  trackLongTasks: true,
  trackUserInteractions: false,
  trackResources: true,
  enableFetchLifecycleActions: false,
  ignoreErrorMessages: DEFAULT_IGNORE_ERROR_MESSAGES,
  enableLogs: false,
  logSampleRate: 100,
  forwardErrorsToLogs: true,
}

/** Joins the parts of a log name, e.g. `api_middleware | fetchOrderList`. */
export const LOG_NAME_SEPARATOR = " | "

/** Log message used for every custom (non-API) error, so DD dashboards can group on it. */
export const CUSTOM_ERROR = "CUSTOM_ERROR"

/**
 * Log message emitted once per page load, so log-based session-rate dashboards
 * have a denominator that is not itself made of errors.
 * See `emitSessionStart` in `utils/datadog`.
 */
export const SESSION_START = "SESSION_START"

export const LOG_COMPONENT_STATE = {
  view: "VIEW",
} as const

let fetchLifecycleEnabled = false

/** Reads GrabX `portalDatadogRumConfig.enableFetchLifecycleActions` (see `configureFetchLifecycleRum`). */
export const configureFetchLifecycleRum = (
  config?: GrabXRUMConfig | null,
): void => {
  fetchLifecycleEnabled = config?.enableFetchLifecycleActions ?? false
}

/** Gates Datadog RUM fetch-lifecycle actions and Scribe `FETCH_LIFECYCLE_LOAD_TIME`. */
export const isFetchLifecycleEnabled = (): boolean => fetchLifecycleEnabled

export function buildRegexList(patterns: string[]): RegExp[] {
  try {
    return patterns
      .map(e => {
        try {
          return new RegExp(e)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "Error compiling individual regex entry from config. Skipping.",
            error,
          )
          return undefined
        }
      })
      .filter(e => e !== undefined) as RegExp[]
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error compiling regex from config. Skipping.", error)
    return []
  }
}

export function getIgnoreErrorMessagesRegex(
  config?: Pick<GrabXRUMConfig, "ignoreErrorMessages">,
): RegExp[] {
  const ignoreErrorMessages =
    config?.ignoreErrorMessages ?? DEFAULT_IGNORE_ERROR_MESSAGES
  return buildRegexList(ignoreErrorMessages)
}
